import { isEqual } from 'lodash';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { useTranslation } from 'react-i18next';
import { useModelRunCredits } from '@/services/CreditsContext';
import { useWorkflowStore } from '@/state/workflow.state';
import { useGlobalStore } from '@/state/global.state';
import { I18N_KEYS } from '@/language/keys';
import { FF_FIGMA_THEME, FF_WORKSPACE_CUSTOM_FONTS_WASM } from '@/consts/featureFlags';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useThemeMode } from '@/contexts/ThemeModeContext';
import { getNodeOriginalName, stackNodesAndEdgesForRendering } from '../Nodes/utils/nodeUtils';
import { WebGLNotSupportedPage } from '../Errors/ErrorPage/WebGLNotSupportedPage';
import { ErrorPage } from '../Errors/ErrorPage/ErrorPage';
import { GPUDriverErrorPage } from '../Errors/ErrorPage/GPUDriverErrorPage';
import { FlowGraph, FlowErrorType } from './FlowGraph';
import { convertParameterValueToLegacyValue, ParameterView } from './Views/ParameterView/ParameterView';
import { CompositorView } from './Views/CompositorView';
import { BlurView } from './Views/BlurView';
import { ComparisonView } from './Views/ComparisonView';
import { BlendView } from './Views/BlendView';
import { VideoConcatenatorView } from './Views/VideoConcatenatorView';
import { DilationErosionView } from './Views/DilationErosionView';
import { ChannelsView } from './Views/ChannelsView';
import { FlowView } from './Views/FlowView';
import { ModelBaseView } from './Views/ModelBaseView';
import { LevelsView } from './Views/LevelsView';
import { ColorCorrectionView } from './Views/ColorCorrectionView';
import { CustomGlslView } from './Views/CustomGlslView';
import { TextView } from './Views/TextView';
import { PromptView } from './Views/PromptView';
import { NumberView } from './Views/NumberView';
import { ImportView } from './Views/ImportView';
import { ResizeView } from './Views/ResizeView';
import { RotateView } from './Views/RotateView';
import { PromptConcatView } from './Views/PromptConcatView';
import { ListSelectorView } from './Views/ListSelectorView';
import { NumberSelectorView } from './Views/NumberSelectorView';
import { StringArrayView } from './Views/StringArrayView';
import { ExportView } from './Views/ExportView';
import { ToggleView } from './Views/ToggleView';
import { SeedView } from './Views/SeedView';
import { CropView } from './Views/CropView';
import { TrimView } from './Views/TrimView';
import { VideoView } from './Views/VideoView';
import { LoraView } from './Views/LoraView';
import { PainterView } from './Views/PainterView';
import { ColorPaletteView } from './Views/ColorPaletteView';
import { MediaIteratorView } from './Views/MediaIteratorView';
import { CustomGroupView } from './Views/CustomGroupView';
import { GroupsManagerView } from './Views/NodeGrouping/GroupsManagerView';
import { MaskExtractorView } from './Views/MaskExtractorView';
import { ImportModelView } from './Views/ImportModelView';
import { FigmaNodeView } from './Views/FigmaNodeView';
import { StickyNoteView } from './Views/StickyNoteView';
import { NodeAssetManager } from './Views/NodeAssetManager';
import { ThreeDView } from './Views/ThreeDView';
import { PointCloudView } from './Views/PointCloudView';
import { PreviewView } from './Views/PreviewView';
import { KlingElementView } from './Views/KlingElementView';
import { KeyframeView } from './Views/KeyframeView';
import { useEditorStore } from './FlowComponents/Editor/editor.state';
import { formatCompositorAiError } from './CompositorAiTasks/compositorAiError';
import type { CustomFontEntry, EvaluatedImage, EvaluatedValue, NodeId, InputId } from 'web';
import type { Node, Edge, BaseNodeData } from '@/types/node';
import type { FlowUIState, FlowWarningType } from './FlowGraph';

let canvasInst: HTMLCanvasElement | null;
function getCanvas() {
  if (canvasInst != null) return canvasInst;
  canvasInst = document.createElement('canvas');
  canvasInst.style.visibility = 'hidden';
  canvasInst.style.position = 'absolute';
  canvasInst.style.top = '100vh';
  canvasInst.style.width = '1024px';
  canvasInst.style.height = '1024px';
  return canvasInst;
}

export function useCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(getCanvas());
  const setParent = useCallback((parent: Element) => {
    const canvas = canvasRef.current;
    if (canvas.parentElement !== parent) {
      parent.appendChild(canvas);
    }
    canvas.style.visibility = 'visible';
    canvas.style.position = '';
    canvas.style.top = '';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
  }, []);

  const unsetParent = useCallback((parent: Element) => {
    const canvas = canvasRef.current;
    if (canvas.parentElement !== parent) {
      return;
    }
    parent.removeChild(canvas);
    canvas.style.visibility = 'hidden';
    canvas.style.position = 'absolute';
    canvas.style.top = '100vh';
    canvas.style.width = '1024px';
    canvas.style.height = '1024px';

    canvasRef.current = canvas;
  }, []);

  return { canvasRef, setParent, unsetParent };
}

const FlowContext = createContext<{ graph: FlowGraph } | null>(null);
export const StorybookFlowContext = FlowContext;

export function useFlowContext() {
  const ctx = useContext(FlowContext);
  if (!ctx) throw new Error('Missing FlowContext.Provider in the tree');
  return ctx;
}

export function useVideoState(nodeId: NodeId, index: number = 0) {
  return useFlowState((state) => state.video[nodeId]?.[index]);
}

export function useComparisonInteracting(nodeId: NodeId) {
  return useFlowState((state) => state.comparison[nodeId]?.interacting) ?? false;
}

export function useFlowState<T>(selector: (state: FlowUIState) => T): T {
  const { graph } = useFlowContext();
  const snapshot = useRef<T>(undefined);
  const getSnapshot = useCallback(() => {
    const newValue = selector(graph.getUIState());
    if (!isEqual(newValue, snapshot.current)) {
      snapshot.current = newValue;
    }
    return snapshot.current!;
  }, [graph, selector]);

  const subscribe = useCallback((f: () => void) => graph.addUIStateListener(f), [graph]);
  return useSyncExternalStore(subscribe, getSnapshot);
}

/**
 * A *reactive* hook that returns the nodes of the flow graph.
 *
 * @example
 * ```tsx
 * const nodes = useNodes();
 * nodes.filter((node) => node.type === 'blurV2');
 * ```
 *
 * @returns The nodes of the flow graph
 */
export function useNodes(): Node[] {
  const { graph } = useFlowContext();
  const subscribe = useCallback((f: () => void) => graph.store.addNodesListener(f), [graph]);
  return useSyncExternalStore(subscribe, () => graph.store.getNodes());
}

const NO_NODES: Node[] = [];

/** The canvas graph, or undefined outside a `FlowContext.Provider` (the `/tool` page has none). */
export function useOptionalFlowGraph(): FlowGraph | undefined {
  return useContext(FlowContext)?.graph;
}

/**
 * {@link useNodes} for a tree that may render outside a `FlowContext.Provider` — the `/tool` page has
 * no graph. Yields no nodes there instead of throwing.
 *
 * TODO: temporary. Its only caller is `usePublishWorkflowNodes`; remove this hook once the
 * status-only Community banner stops running the publish-gating hooks (see the TODO in
 * `useWorkflowCommunityPublishReadiness`).
 */
export function useOptionalNodes(): Node[] {
  const graph = useOptionalFlowGraph();
  const subscribe = useCallback((f: () => void) => (graph ? graph.store.addNodesListener(f) : () => {}), [graph]);
  return useSyncExternalStore(subscribe, () => (graph ? graph.store.getNodes() : NO_NODES));
}

export function useSelectedNodeIds(): readonly NodeId[] {
  const { graph } = useFlowContext();
  const last = useRef<readonly NodeId[]>([]);
  const subscribe = useCallback((f: () => void) => graph.store.addNodesListener(f), [graph]);

  return useSyncExternalStore(subscribe, () => {
    const next = graph.getSelectedNodes();
    if (!isEqual(next, last.current)) last.current = next;
    return last.current;
  });
}

/**
 * A *reactive* hook for the ids of every node, which changes identity only when the node set's
 * *shape* changes — a node added, removed, retyped, or pointed at a different model. Position is
 * deliberately excluded.
 *
 * `useNodes` hands back a new array on every node change, and that includes every frame of a drag.
 * Anything whose answer can only change with the shape — the App Builder's output eligibility, say,
 * which costs WASM calls per node — would otherwise redo all of it sixty times a second while
 * someone nudges a node.
 *
 * @returns The node ids, stable until the shape changes
 */
export function useNodeShapeIds(): readonly NodeId[] {
  const { graph } = useFlowContext();
  const last = useRef<{ key: string; ids: readonly NodeId[] }>({ key: '', ids: [] });
  const subscribe = useCallback((f: () => void) => graph.store.addNodesListener(f), [graph]);

  return useSyncExternalStore(subscribe, () => {
    const nodes = graph.store.getNodes();
    // Type and model are in the key because eligibility reads a node's declared outputs, which both
    // can change without the id set moving.
    const key = nodes.map((node) => `${node.id}:${node.type ?? ''}:${node.data?.model?.name ?? ''}`).join('|');
    if (key !== last.current.key) {
      last.current = { key, ids: nodes.map((node) => node.id) };
    }
    return last.current.ids;
  });
}

/**
 * A *reactive* key of which nodes are locked (`data.isLocked`, what core reads), unchanged until a
 * lock flips — not on every drag frame, as {@link useNodes} would. For memos whose answer depends on
 * locks but that read the nodes through a getter.
 */
export function useLockedNodeIdsKey(): string {
  const { graph } = useFlowContext();
  const subscribe = useCallback((f: () => void) => graph.store.addNodesListener(f), [graph]);
  return useSyncExternalStore(subscribe, () =>
    graph.store
      .getNodes()
      .filter((node) => node.data?.isLocked)
      .map((node) => node.id)
      .join('|'),
  );
}

/**
 * A *reactive* hook that returns the nodes and edges stacked for proper z-ordering in rendering.
 * @returns The nodes and edges with z-index properly set for rendering
 */
export function useStackedNodesAndEdgesForRendering(): {
  nodes: (Node & { zIndex?: number })[];
  edges: Edge[];
} {
  const nodes = useNodes();
  const edges = useEdges();
  return useMemo(() => stackNodesAndEdgesForRendering(nodes, edges), [nodes, edges]);
}

export function useNode(id: NodeId): Node {
  const { graph } = useFlowContext();
  const subscribe = useCallback((f: () => void) => graph.store.addNodeListener(id, f), [graph, id]);
  return useSyncExternalStore(subscribe, () => graph.store.getNode(id));
}

export function useNodeNullable(id: NodeId | undefined): Node | undefined {
  const { graph } = useFlowContext();
  return useSyncExternalStore(
    (f) => (id ? graph.store.addNodeListener(id, f) : () => {}),
    () => (id ? graph.store.getNodeNullable(id) : undefined),
  );
}

export function useNodeData<T extends BaseNodeData>(id: NodeId): T {
  const { graph } = useFlowContext();
  const subscribe = useCallback((f: () => void) => graph.store.addNodeListener(id, f), [graph, id]);
  return useSyncExternalStore(subscribe, () => graph.store.getNodeData<T>(id));
}

/**
 * Subscribes to node updates but only snapshots `Node.originalName` (no full node in component state).
 */
export function useNodeOriginalName(nodeId: NodeId | undefined): string | undefined {
  const { graph } = useFlowContext();
  const subscribe = useCallback(
    (f: () => void) => (nodeId ? graph.store.addNodeListener(nodeId, f) : () => {}),
    [graph, nodeId],
  );
  const getSnapshot = useCallback(() => {
    if (!nodeId) return undefined;
    const node = graph.store.getNodeNullable(nodeId);
    if (!node) return undefined;
    return getNodeOriginalName(node);
  }, [graph, nodeId]);
  return useSyncExternalStore(subscribe, getSnapshot);
}

export function useIsNodeInGroup(nodeId: NodeId): boolean {
  const node = useNodeNullable(nodeId);
  return !!node?.parentId;
}

/**
 * A *reactive* hook that returns the effective lock state of a node.
 * This considers parent group lock state - if the node is in a locked group,
 * it returns true even if the node itself is not locked.
 *`
 * @param nodeId The ID of the node to check
 * @returns The effective lock state (true if locked, false otherwise)
 */
export function useEffectiveIsLocked(nodeId: NodeId | undefined): boolean {
  const { graph } = useFlowContext();
  // Track the current parent ID reactively so we re-subscribe when it changes
  const currentParentId = useNodeNullable(nodeId)?.parentId;
  const subscribe = useCallback(
    (f: () => void) => {
      // Subscribe to the node itself
      if (!nodeId) {
        return () => {};
      }
      const unsubscribeNode = graph.store.addNodeListener(nodeId, f);
      // Also subscribe to parent if it exists
      const unsubscribeParent = currentParentId ? graph.store.addNodeListener(currentParentId, f) : () => {};
      // Return combined unsubscribe function
      return () => {
        unsubscribeNode();
        unsubscribeParent();
      };
    },
    [graph, nodeId, currentParentId],
  );
  return useSyncExternalStore(subscribe, () => (nodeId ? graph.getEffectiveIsLocked(nodeId) : false));
}

export function useNodeDataNullable<T extends BaseNodeData>(id: NodeId | undefined): T | undefined {
  const { graph } = useFlowContext();
  return useSyncExternalStore(
    (f) => (id ? graph.store.addNodeListener(id, f) : () => {}),
    () => (id ? graph.store.getNodeDataNullable<T>(id) : undefined),
  );
}

/**
 * A *reactive* hook that returns the edges of the flow graph.
 *
 * @example
 * ```tsx
 * const edges = useEdges();
 * edges.filter((edge) => edge.source === 'node-id');
 * ```
 *
 * @returns The edges of the flow graph
 */
export function useEdges(): Edge[] {
  const { graph } = useFlowContext();
  const subscribe = useCallback((f: () => void) => graph.store.addEdgesListener(f), [graph.store]);
  return useSyncExternalStore(subscribe, () => graph.store.getEdges());
}

/**
 * A *non-reactive* hook that returns a function that returns the nodes of the flow graph.
 *
 * @example
 * ```tsx
 * const getNodes = useGetNodes();
 * getNodes().filter((node) => node.type === 'blurV2');
 * ```
 *
 * @returns A function that returns the nodes of the flow graph
 */
export function useGetNodes() {
  const { graph } = useFlowContext();
  return useCallback(() => graph.store.getNodes(), [graph]);
}

/**
 * A *non-reactive* hook that returns a function that returns the edges of the flow graph.
 *
 * @example
 * ```tsx
 * const getEdges = useGetEdges();
 * getEdges().filter((edge) => edge.source === 'node-id');
 * ```
 *
 * @returns A function that returns the edges of the flow graph
 */
export function useGetEdges() {
  const { graph } = useFlowContext();
  return useCallback(() => graph.store.getEdges(), [graph]);
}

export function useNodeRenderTarget(id: NodeId, index: number = 0) {
  const { graph } = useFlowContext();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      graph.wasm.call((wasm) => {
        wasm.onWheel(e);
      });
    };

    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener('wheel', onWheel);
    }
    graph.wasm.call((wasm) => {
      wasm.setNodeCanvas(id, index, canvas ?? undefined);
    });
    return () => {
      if (canvas) {
        canvas.removeEventListener('wheel', onWheel);
      }
      graph.wasm.call((wasm) => {
        wasm.setNodeCanvas(id, index, undefined);
      });
    };
  }, [graph, id, index]);

  return canvasRef;
}

export function useNodeValue(nodeId: NodeId, index: number = 0) {
  const { graph } = useFlowContext();
  const snapshot = useRef<EvaluatedValue>(undefined);
  const getSnapshot = useCallback(() => {
    const newValue = graph.evaluateNode(nodeId, index);
    if (!isEqual(newValue, snapshot.current)) {
      snapshot.current = newValue;
    }
    return snapshot.current;
  }, [graph, index, nodeId]);

  const subscribe = useCallback((f: () => void) => graph.addNodeInputListener(nodeId, f), [graph, nodeId]);
  return useSyncExternalStore(subscribe, getSnapshot);
}

export function useNodeStringValue(nodeId: NodeId, index: number = 0): string | undefined {
  const nodeValue = useNodeValue(nodeId, index);
  if (nodeValue && nodeValue.type !== 'string') {
    throw new Error(`unexpected value type "${nodeValue.type}" for node ${nodeId}`);
  }
  return nodeValue?.value;
}

export function useNodeStringArrayValue(nodeId: NodeId, index: number = 0): string[] {
  const nodeValue = useNodeValue(nodeId, index);
  if (nodeValue && nodeValue.type !== 'string_array') {
    throw new Error(`unexpected value type "${nodeValue.type}" for node ${nodeId}`);
  }
  return useMemo(() => nodeValue?.value ?? [], [nodeValue]);
}

function useNodeInputStore<T>(nodeId: NodeId, select: () => T): T {
  const { graph } = useFlowContext();
  const snapshotRef = useRef<T>(undefined as unknown as T);

  const getSnapshot = useCallback(() => {
    const next = select();
    if (!isEqual(next, snapshotRef.current)) snapshotRef.current = next;
    return snapshotRef.current;
  }, [select]);

  const subscribe = useCallback(
    (onChange: () => void) => graph.addNodeInputListener(nodeId, onChange),
    [graph, nodeId],
  );

  return useSyncExternalStore(subscribe, getSnapshot);
}

/** Calls `onChange` on any change to the dependencies of this node in the graph.
 * If the `immediate` option is set, it will be called before subscribing to changes.
 * Notes:
 * - ongoing changes do not trigger the callback (i.e. dragging a slider will trigger this only at the end of the drag)
 * - the callback will run even if the evaluated inputs haven't changed. this is intentional.
 * - pass `inputId` to only react to changes on that specific input; omit it to react to any input.
 * */
export function useOnNodeInputChange(
  nodeId: NodeId,
  onChange: () => void,
  options?: { immediate?: boolean; inputId?: InputId },
) {
  const { graph } = useFlowContext();
  useEffect(() => {
    if (options?.immediate) onChange();
    return graph.addNodeInputListener(nodeId, onChange, options?.inputId);
  }, [nodeId, graph, onChange, options?.immediate, options?.inputId]);
}

/**
 * Reactive hook that returns the value of the given input for a node
 * Causes a component to rerender if upstream changes to the graph changes the input value
 * Will crash if given input id doesn't exist for the node.
 */
export function useNodeInput(nodeId: NodeId, inputId: InputId): EvaluatedValue | undefined {
  const { graph } = useFlowContext();
  const select = useCallback(() => graph.evaluateInput(nodeId, inputId), [graph, nodeId, inputId]);
  return useNodeInputStore(nodeId, select);
}

/**
 * Reactive hook that returns the evaluated image for a given input, or undefined if no image is connected.
 * Causes a component to rerender when the image value changes.
 */
export function useNodeImageValue(nodeId: NodeId, inputId: InputId): EvaluatedImage | undefined {
  const value = useNodeInput(nodeId, inputId);
  return value?.type === 'image' ? value.value : undefined;
}

/**
 * Reactive hook that returns an map of with *all* inputs to the node
 * Causes a component to rerender if upstream changes to the graph changes any input value
 * Will crash if given input id doesn't exist for the node.
 */
export function useNodeInputs(nodeId: NodeId): Record<InputId, EvaluatedValue | undefined> {
  const { graph } = useFlowContext();
  const select = useCallback(
    () => Object.fromEntries(graph.getInputIds(nodeId).map((id) => [id, graph.evaluateInput(nodeId, id)])),
    [graph, nodeId],
  );
  return useNodeInputStore(nodeId, select);
}

function resolveNodeParams(graph: FlowGraph, nodeId: NodeId, keys: readonly string[]): Record<string, unknown> {
  // The properties panel can still render while a deleted node is unmounting.
  if (!graph.store.getNodeNullable(nodeId)) return {};

  const parameterIds = new Set(graph.wasm.call((wasm) => wasm.parameterIds(nodeId)) ?? []);
  return Object.fromEntries(
    keys
      .filter((key) => parameterIds.has(key))
      .map((key) => {
        const value = graph.wasm.call((wasm) => wasm.evaluateParameter(nodeId, key));
        return [key, value ? convertParameterValueToLegacyValue(value) : undefined];
      }),
  );
}

/**
 * Reactive hook that returns the resolved values of the given parameter keys for a node, using
 * the same WASM path used right before a run. Re-renders only when one of those resolved values
 * actually changes — including when an extracted parameter (e.g. resolution) is wired to a
 * different upstream value.
 */
export function useNodeParams(nodeId: NodeId, keys: readonly string[]): Record<string, unknown> {
  const { graph } = useFlowContext();
  const select = useCallback(() => resolveNodeParams(graph, nodeId, keys), [graph, nodeId, keys]);
  return useNodeInputStore(nodeId, select);
}

/**
 * Multi-node variant of {@link useNodeParams}. Subscribes to each node's input listener and
 * returns one resolved-params record per node id (parallel array). Re-renders only when one of
 * the resolved values actually changes.
 */
export function useNodesParams(nodeIds: readonly NodeId[], keys: readonly string[]): Record<string, unknown>[] {
  const { graph } = useFlowContext();
  const idsKey = nodeIds.join(',');
  const subscribe = useCallback(
    (onChange: () => void) => {
      const unsubs = nodeIds.map((id) => graph.addNodeInputListener(id, onChange));
      return () => unsubs.forEach((u) => u());
    },
    // idsKey captures nodeIds-by-value; nodeIds itself is typically a fresh array each render.
    // eslint-disable-next-line react-hooks-js/exhaustive-deps
    [graph, idsKey],
  );
  const snapshotRef = useRef<Record<string, unknown>[]>([]);
  const getSnapshot = useCallback(
    () => {
      const next = nodeIds.map((id) => resolveNodeParams(graph, id, keys));
      if (!isEqual(next, snapshotRef.current)) snapshotRef.current = next;
      return snapshotRef.current;
    },
    // eslint-disable-next-line react-hooks-js/exhaustive-deps
    [graph, idsKey, keys],
  );
  return useSyncExternalStore(subscribe, getSnapshot);
}

export type InputChange =
  | { inputId: InputId; value: EvaluatedValue; changeType: 'added' | 'updated' }
  | { inputId: InputId; value: undefined; changeType: 'deleted' };

export function useOnInputsChange(nodeId: NodeId, onChange: (changes: InputChange[]) => void) {
  const { graph } = useFlowContext();
  const prevRef = useRef<Map<InputId, EvaluatedValue>>(new Map());

  useEffect(() => {
    prevRef.current = snapshotInputs(graph, nodeId);

    return graph.addNodeInputListener(nodeId, () => {
      const next = snapshotInputs(graph, nodeId);
      const changes = diffSnapshots(prevRef.current, next);
      prevRef.current = next;
      if (changes.length > 0) onChange(changes);
    });
  }, [graph, nodeId, onChange]);
}

function snapshotInputs(graph: FlowGraph, nodeId: NodeId): Map<InputId, EvaluatedValue> {
  const snapshot = new Map<InputId, EvaluatedValue>();
  for (const id of graph.getInputIds(nodeId)) {
    const value = graph.evaluateInput(nodeId, id);
    if (value !== undefined) {
      snapshot.set(id, value);
    }
  }
  return snapshot;
}

function diffSnapshots(prev: Map<InputId, EvaluatedValue>, next: Map<InputId, EvaluatedValue>): InputChange[] {
  const changes: InputChange[] = [];

  for (const [inputId, value] of next) {
    const prevValue = prev.get(inputId);
    if (prevValue === undefined) {
      changes.push({ inputId, value, changeType: 'added' });
    } else if (!isEqual(prevValue, value)) {
      changes.push({ inputId, value, changeType: 'updated' });
    }
  }

  for (const inputId of prev.keys()) {
    if (!next.has(inputId)) {
      changes.push({ inputId, value: undefined, changeType: 'deleted' });
    }
  }

  return changes;
}

export function useFlowView() {
  const { graph } = useFlowContext();

  return useMemo(() => new FlowView(graph), [graph]);
}

export function useCustomGroupView(groupNodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new CustomGroupView(graph, groupNodeId), [graph, groupNodeId]);
}

export function useVideoView(nodeId: NodeId, index: number = 0) {
  const { graph } = useFlowContext();
  return useMemo(() => new VideoView(graph, nodeId, index), [graph, nodeId, index]);
}

/** Resource keys (URLs) of every video the given node depends on. See {@link FlowGraph.videoDependencies}. */
export function useVideoDependencies(nodeId: NodeId, enabled: boolean): string[] {
  const { graph } = useFlowContext();
  const select = useCallback(() => (enabled ? graph.videoDependencies(nodeId) : []), [graph, nodeId, enabled]);
  return useNodeInputStore(nodeId, select);
}

export function useCompositorView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new CompositorView(graph, nodeId), [graph, nodeId]);
}

export function useBlurView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new BlurView(graph, nodeId), [graph, nodeId]);
}

export function useComparisonView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new ComparisonView(graph, nodeId), [graph, nodeId]);
}

export function useBlendView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new BlendView(graph, nodeId), [graph, nodeId]);
}

export function useVideoConcatenatorView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new VideoConcatenatorView(graph, nodeId), [graph, nodeId]);
}

export function useLevelsView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new LevelsView(graph, nodeId), [graph, nodeId]);
}

export function useColorCorrectionView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new ColorCorrectionView(graph, nodeId), [graph, nodeId]);
}

export function useCustomGlslView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new CustomGlslView(graph, nodeId), [graph, nodeId]);
}

export function useRotateView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new RotateView(graph, nodeId), [graph, nodeId]);
}

export function useDilationErosionView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new DilationErosionView(graph, nodeId), [graph, nodeId]);
}

export function useChannelsView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new ChannelsView(graph, nodeId), [graph, nodeId]);
}

export function useTextView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new TextView(graph, nodeId), [graph, nodeId]);
}

export function usePromptView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new PromptView(graph, nodeId), [graph, nodeId]);
}

export function usePromptConcatView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new PromptConcatView(graph, nodeId), [graph, nodeId]);
}

export function useNumberView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new NumberView(graph, nodeId), [graph, nodeId]);
}

export function useThreeDView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new ThreeDView(graph, nodeId), [graph, nodeId]);
}

export function usePointCloudView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new PointCloudView(graph, nodeId), [graph, nodeId]);
}

export function useStringArrayView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new StringArrayView(graph, nodeId), [graph, nodeId]);
}

export function useImportView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new ImportView(graph, nodeId), [graph, nodeId]);
}

export function useMediaIteratorView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new MediaIteratorView(graph, nodeId), [graph, nodeId]);
}

export function useModelBaseView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new ModelBaseView(graph, nodeId), [graph, nodeId]);
}

export function useParameterView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new ParameterView(graph, nodeId), [graph, nodeId]);
}

export function useCropView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new CropView(graph, nodeId), [graph, nodeId]);
}

export function useResizeView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new ResizeView(graph, nodeId), [graph, nodeId]);
}

export function useTrimView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new TrimView(graph, nodeId), [graph, nodeId]);
}

export function useListSelectorView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new ListSelectorView(graph, nodeId), [graph, nodeId]);
}

export function usePainterView(id: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new PainterView(graph, id), [graph, id]);
}

export function useColorPaletteView(id: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new ColorPaletteView(graph, id), [graph, id]);
}

export function useNumberSelectorView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new NumberSelectorView(graph, nodeId), [graph, nodeId]);
}

export function useExportView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new ExportView(graph, nodeId), [graph, nodeId]);
}

export function usePreviewView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new PreviewView(graph, nodeId), [graph, nodeId]);
}

export function useKlingElementView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new KlingElementView(graph, nodeId), [graph, nodeId]);
}

export function useKeyframeView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new KeyframeView(graph, nodeId), [graph, nodeId]);
}

export function useToggleView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new ToggleView(graph, nodeId), [graph, nodeId]);
}

export function useSeedView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new SeedView(graph, nodeId), [graph, nodeId]);
}

export function useLoraView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new LoraView(graph, nodeId), [graph, nodeId]);
}

export function useMaskExtractorView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new MaskExtractorView(graph, nodeId), [graph, nodeId]);
}

export function useGroupsManagerView() {
  const { graph } = useFlowContext();
  return useMemo(() => new GroupsManagerView(graph), [graph]);
}

export function useNodeAssetManager() {
  const { graph } = useFlowContext();
  return useMemo(() => new NodeAssetManager(graph), [graph]);
}

export function useImportModelView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new ImportModelView(graph, nodeId), [graph, nodeId]);
}

export function useFigmaNodeView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new FigmaNodeView(graph, nodeId), [graph, nodeId]);
}

export function useStickyNoteView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new StickyNoteView(graph, nodeId), [graph, nodeId]);
}

type FlowProviderProps = React.PropsWithChildren<{
  nodes: Node[];
  edges: Edge[];
  workflowCustomFontsUrlsMap: ReadonlyMap<string, string>;
  /** Workspace fonts available in this workflow. Pushed into the engine via `setCustomFonts` so `Font::Custom` layers can resolve to bytes. */
  workflowCustomFontEntries: CustomFontEntry[];
  /** When true, font download/deserialize warnings do not open the global snackbar (e.g. missing-workflow-fonts modal already explains the situation). */
  suppressFontResourceWarningSnackbar?: boolean;
}>;
export function FlowProvider({
  nodes: initialNodes,
  edges: initialEdges,
  workflowCustomFontsUrlsMap,
  workflowCustomFontEntries,
  suppressFontResourceWarningSnackbar = false,
  children,
}: FlowProviderProps) {
  const updateModelCredits = useModelRunCredits();
  const { canvasRef } = useCanvas();
  const themeMode = useThemeMode();
  const setLoadingState = useWorkflowStore((state) => state.setLoadingState);
  const role = useWorkflowStore((state) => state.workflowRole);
  const { t } = useTranslation();
  const { value: isFigmaThemeEnabled } = useFeatureFlag(FF_FIGMA_THEME);
  const figmaThemeEnabled = isFigmaThemeEnabled === true;
  const { value: isCustomFontsWasmEnabled } = useFeatureFlag(FF_WORKSPACE_CUSTOM_FONTS_WASM);

  const [graph, setGraph] = useState<FlowGraph | undefined>();
  useLayoutEffect(() => {
    if (graph) graph.compositorAiTasks.onCredits = updateModelCredits;
  }, [graph, updateModelCredits]);
  const [errorType, setErrorType] = useState<FlowErrorType | undefined>(undefined);
  const [warningType, setWarningType] = useState<FlowWarningType | undefined>(undefined);
  const closeEditor = useEditorStore((selector) => selector.onClose);

  // FlowGraph doesn't keep its own copy of the catalog; it reads the current entries
  // from here when it needs to restore them onto a fresh wasm instance after panic recovery.
  const customFontEntriesRef = useRef<CustomFontEntry[]>([]);
  const getCustomFontEntries = useCallback(() => customFontEntriesRef.current, []);

  useEffect(() => {
    let newGraph: FlowGraph | undefined;
    const initGraph = () => {
      setErrorType(undefined);
      setLoadingState('flow-graph', 'loading');
      // FlowGraph constructor is a long and synchronous operation, so we need to set the loading state before and after
      newGraph = new FlowGraph(
        initialNodes,
        initialEdges,
        canvasRef.current,
        role === 'editor',
        setErrorType,
        setWarningType,
        workflowCustomFontsUrlsMap,
        getCustomFontEntries,
        figmaThemeEnabled,
      );
      setGraph(newGraph);
      setLoadingState('flow-graph', 'loaded');
    };
    initGraph();
    return () => {
      newGraph?.dispose();
      setGraph(undefined);
    };
    // Mount-only: constructor inputs (nodes/edges, canvas, role, fonts map, theme) are latched
    // from this first render. Later prop changes must not rebuild a live graph — that would
    // re-seed from whatever the parent still holds (often the first-fetch snapshot) and drop
    // in-session edits. Intentional replace is a new FlowProvider instance (parent `key`).
    // Role is flipped live via `graph.setEditable` below; font URL map via `loadFontResources`.
    // eslint-disable-next-line react-hooks-js/exhaustive-deps
  }, []);

  useEffect(() => {
    graph?.wasm.setPreviewLightMode(themeMode === 'light');
  }, [graph, themeMode]);

  // Flip WASM canvas editability in place when the role changes (Version History guest mode),
  // rather than rebuilding the graph.
  useEffect(() => {
    graph?.setEditable(role === 'editor');
  }, [graph, role]);

  // Keep the FlowGraph's font URL map in sync when `workflowCustomFontsUrlsMap` identity changes
  // (e.g. the workspace-custom-fonts FF flips mid-session and `useWorkflowFontsQuery` re-resolves
  // with new presigned URLs). `loadFontResources` swaps the map in place and asks wasm which font
  // keys need loading — no reconstruction, no edit loss.
  useEffect(() => {
    graph?.loadFontResources(workflowCustomFontsUrlsMap);
  }, [graph, workflowCustomFontsUrlsMap]);

  // Gate the engine render path behind its own flag so it ramps independently of
  // workspace-custom-fonts (which drives the fonts query + missing-fonts UX). With the flag off,
  // push an empty catalog → `Font::Custom` layers resolve to DEFAULT_FONT_KEY (DMSans fallback).
  useEffect(() => {
    if (!graph) {
      return;
    }
    const entries = isCustomFontsWasmEnabled === true ? workflowCustomFontEntries : [];
    customFontEntriesRef.current = entries;
    graph.setCustomFontEntries(entries);
  }, [graph, workflowCustomFontEntries, isCustomFontsWasmEnabled]);

  const updateSnackbarData = useGlobalStore((state) => state.updateSnackbarData);
  useLayoutEffect(() => {
    if (!graph) return;
    graph.compositorAiTasks.onError = (task) => {
      updateSnackbarData({
        key: task.id,
        text: formatCompositorAiError(task.error, t),
        severity: 'error',
        isOpen: true,
      });
    };
    return () => {
      graph.compositorAiTasks.onError = undefined;
    };
  }, [graph, updateSnackbarData, t]);
  useLayoutEffect(() => {
    if (errorType === 'recovered') {
      closeEditor();
      setErrorType(undefined);
    }
  }, [errorType, closeEditor]);

  useEffect(() => {
    let text = '';
    switch (warningType) {
      case 'failed-deserialize-3d-model':
        text = t(I18N_KEYS.FLOW_WARNINGS.FAILED_DESERIALIZE_3D_MODEL);
        break;
      case 'failed-deserialize-font':
        text = t(I18N_KEYS.FLOW_WARNINGS.FAILED_DESERIALIZE_FONT);
        break;
      case 'failed-deserialize-image':
        text = t(I18N_KEYS.FLOW_WARNINGS.FAILED_DESERIALIZE_IMAGE);
        break;
      case 'failed-deserialize-video':
        text = t(I18N_KEYS.FLOW_WARNINGS.FAILED_DESERIALIZE_VIDEO);
        break;
      case 'failed-download-image':
        text = t(I18N_KEYS.FLOW_WARNINGS.FAILED_DOWNLOAD_IMAGE);
        break;
      case 'failed-download-video':
        text = t(I18N_KEYS.FLOW_WARNINGS.FAILED_DOWNLOAD_VIDEO);
        break;
      case 'failed-download-audio':
        text = t(I18N_KEYS.FLOW_WARNINGS.FAILED_DOWNLOAD_AUDIO);
        break;
      case 'failed-download-font':
        text = t(I18N_KEYS.FLOW_WARNINGS.FAILED_DOWNLOAD_FONT);
        break;
      case 'failed-download-3d-model':
        text = t(I18N_KEYS.FLOW_WARNINGS.FAILED_DOWNLOAD_3D_MODEL);
        break;
      default:
        return;
    }

    const SUPPRESSED_FONTS_WARNING_TYPES = ['failed-deserialize-font', 'failed-download-font'];
    const suppressSnackbar =
      suppressFontResourceWarningSnackbar && SUPPRESSED_FONTS_WARNING_TYPES.includes(warningType);

    if (!suppressSnackbar) {
      updateSnackbarData({
        text,
        icon: null,
        isOpen: true,
        duration: 5000,
        severity: 'error',
      });
    }
    setWarningType(undefined);
  }, [warningType, updateSnackbarData, t, suppressFontResourceWarningSnackbar]);

  switch (errorType) {
    case 'fatal':
      return <ErrorPage />;
    case 'webgl-not-supported':
      return <WebGLNotSupportedPage />;
    case 'context-lost':
      return <GPUDriverErrorPage />;
    case 'recovered':
      return null; // Need to rerender the entire React tree
    default:
  }

  if (!graph) {
    return null;
  }

  return <FlowContext.Provider value={{ graph }}>{children}</FlowContext.Provider>;
}
